import { CopyButton } from "@components/shared/CopyButton";
import {
  GENERATE_PERSONAS,
  TOGGLE_CAMPAIGN_BRIEF_LOCK,
  UPDATE_CAMPAIGN_BRIEF,
  UPDATE_PERSONAS,
} from "@lib/graphql/mutations";
import { CAMPAIGN_BRIEF_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  Banknote,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  GitCompareArrows,
  Lock,
  Pencil,
  Sparkles,
  Unlock,
  Users,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";
import { BriefEditor, type RoleProfileFields, type SalaryFields } from "./BriefEditor";
import { BriefRenderer } from "./BriefRenderer";
import { BriefVersionDiff } from "./BriefVersionDiff";
import { BriefVersionHistory } from "./BriefVersionHistory";
import type { Persona, Version } from "./briefTypes";
import { type Stakeholder, StakeholdersEditor } from "./StakeholdersEditor";
import { StalenessBanner } from "./StalenessBanner";
import { PersonasEditor } from "./stages/PersonasEditor";

function briefCopyContent(el: HTMLElement | null): { html: string; text: string } {
  if (!el) return { html: "", text: "" };
  return { html: el.innerHTML, text: el.innerText || el.textContent || "" };
}

interface BriefSectionProps {
  campaignId: string;
}

export function BriefSection({ campaignId }: BriefSectionProps) {
  const [editing, setEditing] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<Version | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [editingPersonas, setEditingPersonas] = useState(false);
  const [personaGenerateMessage, setPersonaGenerateMessage] = useState<{
    kind: "error" | "info";
    text: string;
  } | null>(null);
  const currentBodyRef = useRef<HTMLDivElement>(null);
  const historyBodyRef = useRef<HTMLDivElement>(null);

  const [{ data, fetching }, reexecute] = useQuery({
    query: CAMPAIGN_BRIEF_QUERY,
    variables: { id: campaignId },
  });
  const [{ fetching: saving }, updateBrief] = useMutation(UPDATE_CAMPAIGN_BRIEF);
  const [{ fetching: toggling }, toggleLock] = useMutation(TOGGLE_CAMPAIGN_BRIEF_LOCK);
  const [{ fetching: savingPersonas }, updatePersonas] = useMutation(UPDATE_PERSONAS);
  const [{ fetching: generatingPersonas }, generatePersonas] = useMutation(GENERATE_PERSONAS);
  const refreshBrief = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refreshBrief);

  const brief = data?.campaign?.brief;
  const briefId: string | undefined = brief?.id;
  const currentVersion: Version | undefined = brief?.currentVersion;
  const versions: Version[] = brief?.versions ?? [];
  const locked: boolean = brief?.locked ?? false;
  const lockedByName: string | null = brief?.lockedBy?.fullName ?? null;
  const stakeholders: Stakeholder[] = (brief?.stakeholders ?? []).map(
    (s: {
      id: string;
      stakeholderRole: string;
      userId: string;
      user: { id: string; fullName: string } | null;
    }) => ({
      id: s.id,
      stakeholderRole: s.stakeholderRole,
      userId: s.userId,
      userName: s.user?.fullName ?? null,
    }),
  );
  const teamMembers: { id: string; fullName: string }[] = data?.campaign?.teamMembers ?? [];
  const personas: Persona[] = brief?.personas ?? [];
  const handleToggleLock = async () => {
    await toggleLock({ input: { campaignId } });
    reexecute({ requestPolicy: "network-only" });
  };

  const handleSave = async (
    additionalNotes: string,
    changeSummary: string | undefined,
    salary: SalaryFields,
    roleProfile: RoleProfileFields,
  ) => {
    await updateBrief({
      input: {
        campaignId,
        additionalNotes,
        changeSummary,
        salaryBase: salary.salaryBase ? parseInt(salary.salaryBase, 10) : null,
        salaryBonus: salary.salaryBonus || null,
        salaryEquity: salary.salaryEquity || null,
        salaryBenefits: salary.salaryBenefits || null,
        salaryCurrency: salary.salaryCurrency || null,
        targetStartDate: salary.targetStartDate || null,
        assessmentProcess: salary.assessmentProcess || null,
        backgroundCheckRequirements: salary.backgroundCheckRequirements || null,
        roleTitle: roleProfile.roleTitle || null,
        roleMandate: roleProfile.roleMandate || null,
        contextNotes: roleProfile.contextNotes || null,
        successOutcomes: roleProfile.successOutcomes,
        culturalFitNotes: roleProfile.culturalFitNotes || null,
        motivationFitNotes: roleProfile.motivationFitNotes || null,
        searchCountryCodes: roleProfile.searchCountryCodes,
        searchLocations: roleProfile.searchLocations,
      },
    });
    setEditing(false);
    setViewingVersion(null);
    reexecute({ requestPolicy: "network-only" });
  };

  const handleSavePersonas = async (
    items: { name: string; summary: string; description: string }[],
  ) => {
    if (!briefId) return;
    await updatePersonas({ input: { campaignBriefId: briefId, personas: items } });
    setEditingPersonas(false);
    reexecute({ requestPolicy: "network-only" });
  };

  const handleGeneratePersonas = async () => {
    setPersonaGenerateMessage(null);
    const result = await generatePersonas({ input: { campaignId } });
    if (result.error) {
      setPersonaGenerateMessage({ kind: "error", text: result.error.message });
      return;
    }
    const payload = result.data?.generatePersonas;
    const errors: string[] = payload?.errors ?? [];
    if (errors.length > 0) {
      setPersonaGenerateMessage({ kind: "error", text: errors.join(", ") });
      return;
    }
    if (payload?.enqueued) {
      setPersonaGenerateMessage({
        kind: "info",
        text: "Generating personas — watch the background tasks indicator at the top.",
      });
    }
    // Personas appear via the campaign channel broadcast once the job completes.
  };

  if (fetching && !brief) return <p className="text-sm text-text-muted">Loading brief...</p>;

  if (editing) {
    return (
      <BriefEditor
        additionalNotes={currentVersion?.additionalNotes ?? ""}
        initialSalary={{
          salaryBase: currentVersion?.salaryBase != null ? String(currentVersion.salaryBase) : "",
          salaryBonus: currentVersion?.salaryBonus ?? "",
          salaryEquity: currentVersion?.salaryEquity ?? "",
          salaryBenefits: currentVersion?.salaryBenefits ?? "",
          salaryCurrency: currentVersion?.salaryCurrency ?? "GBP",
          targetStartDate: currentVersion?.targetStartDate ?? "",
          assessmentProcess: currentVersion?.assessmentProcess ?? "",
          backgroundCheckRequirements: currentVersion?.backgroundCheckRequirements ?? "",
        }}
        initialRoleProfile={{
          roleTitle: currentVersion?.roleTitle ?? "",
          roleMandate: currentVersion?.roleMandate ?? "",
          contextNotes: currentVersion?.contextNotes ?? "",
          successOutcomes: currentVersion?.successOutcomes ?? [],
          culturalFitNotes: currentVersion?.culturalFitNotes ?? "",
          motivationFitNotes: currentVersion?.motivationFitNotes ?? "",
          searchCountryCodes: currentVersion?.searchCountryCodes ?? [],
          searchLocations: currentVersion?.searchLocations ?? [],
        }}
        onSave={handleSave}
        onCancel={() => setEditing(false)}
        saving={saving}
        requireChangeSummary={locked}
      />
    );
  }

  if (viewingVersion) {
    const previousVersion = versions.find(
      (v) => v.versionNumber === viewingVersion.versionNumber - 1,
    );

    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-text-secondary">
                Version {viewingVersion.versionNumber}
              </h3>
              <span className="text-xs text-text-muted">
                by {viewingVersion.author.fullName} on{" "}
                {new Date(viewingVersion.effectiveFrom).toLocaleDateString()}
              </span>
              {viewingVersion.changeSummary && (
                <span className="text-xs text-text-faint">— {viewingVersion.changeSummary}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {previousVersion && (
                <button
                  type="button"
                  onClick={() => setShowDiff(!showDiff)}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
                >
                  <GitCompareArrows size={12} />
                  {showDiff ? "Hide changes" : "Show changes"}
                </button>
              )}
              <CopyButton
                ariaLabel="Copy brief"
                withLabel
                size={12}
                getContent={() => briefCopyContent(historyBodyRef.current)}
              />
              <button
                type="button"
                onClick={() => {
                  setViewingVersion(null);
                  setShowDiff(false);
                }}
                className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {showDiff && previousVersion && (
            <div className="mb-3 border-b border-border pb-3">
              <BriefVersionDiff oldVersion={previousVersion} newVersion={viewingVersion} />
            </div>
          )}
          <div ref={historyBodyRef}>
            <RoleProfileSummary version={viewingVersion} />
            <SalarySummary version={viewingVersion} />
            <ClientProcessSummary version={viewingVersion} />
            <AdditionalNotesSection version={viewingVersion} />
          </div>
        </div>
        <BriefVersionHistory
          versions={versions}
          onViewVersion={(v) => {
            setViewingVersion(v);
            setShowDiff(false);
          }}
        />
      </div>
    );
  }

  if (!currentVersion) {
    return (
      <div className="rounded-lg bg-card p-6 text-center shadow-sm ring-1 ring-ring">
        <FileText size={32} className="mx-auto text-text-faint" />
        <p className="mt-2 text-sm text-text-muted">No brief yet</p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-3 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90"
        >
          Create Brief
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StalenessBanner
        campaignId={campaignId}
        kinds={["role_blueprint", "personas"]}
        message={"Some downstream artefacts are out of date relative to the current brief."}
      />
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-text-secondary">Brief</h3>
            <span className="text-xs text-text-faint">v{currentVersion.versionNumber}</span>
            {locked && lockedByName && (
              <span className="text-xs text-text-faint">Locked by {lockedByName}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {versions.length > 1 && (
              <button
                type="button"
                onClick={() => setShowHistory(!showHistory)}
                className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
              >
                {showHistory ? "Hide history" : `${versions.length} versions`}
              </button>
            )}
            <button
              type="button"
              onClick={handleToggleLock}
              disabled={toggling}
              title={locked ? "Unlock brief" : "Lock brief"}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-card-hover ${
                locked ? "text-amber-500" : "text-text-muted"
              }`}
            >
              {locked ? <Lock size={12} /> : <Unlock size={12} />}
              {locked ? "Locked" : "Lock"}
            </button>
            <CopyButton
              ariaLabel="Copy brief"
              withLabel
              size={12}
              getContent={() => briefCopyContent(currentBodyRef.current)}
            />
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
            >
              <Pencil size={12} /> Edit
            </button>
          </div>
        </div>
        <div ref={currentBodyRef}>
          <RoleProfileSummary version={currentVersion} onEdit={() => setEditing(true)} />
          <SalarySummary version={currentVersion} onEdit={() => setEditing(true)} />
          <ClientProcessSummary version={currentVersion} onEdit={() => setEditing(true)} />
          <AdditionalNotesSection version={currentVersion} onEdit={() => setEditing(true)} />
          <IntakeNotesDisclosure version={currentVersion} />
        </div>
      </div>
      {showHistory && <BriefVersionHistory versions={versions} onViewVersion={setViewingVersion} />}

      {/* Personas — first-class artefact, parented to the brief after story 2002. */}
      {briefId && (
        <PersonasPanel
          personas={personas}
          editing={editingPersonas}
          saving={savingPersonas}
          briefDefined={currentVersion != null}
          generating={generatingPersonas}
          generateMessage={personaGenerateMessage}
          onStartEdit={() => setEditingPersonas(true)}
          onCancelEdit={() => setEditingPersonas(false)}
          onSave={handleSavePersonas}
          onGenerate={handleGeneratePersonas}
        />
      )}

      <StakeholdersEditor
        campaignId={campaignId}
        initial={stakeholders}
        teamMembers={teamMembers}
        onSaved={refreshBrief}
      />
    </div>
  );
}

interface PersonasPanelProps {
  personas: Persona[];
  editing: boolean;
  saving: boolean;
  briefDefined: boolean;
  generating: boolean;
  generateMessage: { kind: "error" | "info"; text: string } | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (personas: { name: string; summary: string; description: string }[]) => void;
  onGenerate: () => void;
}

function personasCopyContent(el: HTMLElement | null): { html: string; text: string } {
  if (!el) return { html: "", text: "" };
  return { html: el.innerHTML, text: el.innerText || el.textContent || "" };
}

function PersonasPanel({
  personas,
  editing,
  saving,
  briefDefined,
  generating,
  generateMessage,
  onStartEdit,
  onCancelEdit,
  onSave,
  onGenerate,
}: PersonasPanelProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  if (editing) {
    return (
      <PersonasEditor
        initial={personas.map((p) => ({
          name: p.name,
          summary: p.summary ?? "",
          description: p.description ?? "",
        }))}
        onSave={onSave}
        onCancel={onCancelEdit}
        saving={saving}
      />
    );
  }

  if (personas.length === 0) {
    return (
      <div id="personas" className="rounded-lg bg-card p-6 text-center shadow-sm ring-1 ring-ring">
        <Users size={32} className="mx-auto text-text-faint" />
        <p className="mt-2 text-sm text-text-muted">No personas defined</p>
        <p className="mt-1 text-xs text-text-faint">
          Personas describe the ideal candidate archetypes for this role.
        </p>
        <div className="mt-3 flex items-center justify-center gap-2">
          {briefDefined && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              title="Generate personas from the brief using AI"
              className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles size={14} />
              {generating ? "Generating..." : "Generate personas"}
            </button>
          )}
          <button
            type="button"
            onClick={onStartEdit}
            className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-card-hover"
          >
            Add manually
          </button>
        </div>
        {generateMessage && (
          <p
            className={`mt-3 text-xs ${
              generateMessage.kind === "error" ? "text-red-600" : "text-text-muted"
            }`}
          >
            {generateMessage.text}
          </p>
        )}
      </div>
    );
  }

  return (
    <div id="personas" className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Personas</h3>
          <span className="text-xs text-text-faint">
            {personas.length} {personas.length === 1 ? "persona" : "personas"}
          </span>
        </div>
        <CopyButton
          ariaLabel="Copy personas"
          withLabel
          size={12}
          getContent={() => personasCopyContent(contentRef.current)}
        />
        <button
          type="button"
          onClick={onStartEdit}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          <Pencil size={12} /> Edit
        </button>
      </div>
      <div ref={contentRef} className="space-y-3">
        {personas.map((persona) => (
          <div key={persona.id} className="rounded border border-border p-3">
            <h4 className="text-sm font-medium text-text-primary">{persona.name}</h4>
            {persona.summary && <p className="mt-0.5 text-xs text-text-muted">{persona.summary}</p>}
            {persona.description && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-text-primary">
                {persona.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface SummaryProps {
  version: Version;
  onEdit?: () => void;
}

function EmptyFieldPrompt({ hint, onEdit }: { hint: string; onEdit?: () => void }) {
  const className = "mt-0.5 block text-xs italic text-text-faint";
  if (!onEdit) return <p className={className}>{hint}</p>;
  return (
    <button
      type="button"
      onClick={onEdit}
      className={`${className} w-full text-left hover:text-text-secondary hover:underline`}
    >
      {hint}
    </button>
  );
}

function RoleProfileSummary({ version, onEdit }: SummaryProps) {
  const hasOutcomes = version.successOutcomes && version.successOutcomes.length > 0;

  return (
    <div className="mt-3 border-t border-border pt-3 space-y-2">
      <div className="text-xs font-medium text-text-secondary">Role Profile</div>
      <div>
        <span className="block text-xs text-text-faint">Title</span>
        {version.roleTitle ? (
          <p className="text-sm text-text-primary">{version.roleTitle}</p>
        ) : (
          <EmptyFieldPrompt
            hint="The headline title used in outreach and the job posting."
            onEdit={onEdit}
          />
        )}
      </div>
      <div>
        <span className="block text-xs text-text-faint">Mandate</span>
        {version.roleMandate ? (
          <p className="whitespace-pre-wrap text-sm text-text-primary">{version.roleMandate}</p>
        ) : (
          <EmptyFieldPrompt
            hint="The one-line answer to 'what is this role for?' Anchors the role blueprint."
            onEdit={onEdit}
          />
        )}
      </div>
      <div>
        <span className="block text-xs text-text-faint">Context</span>
        {version.contextNotes ? (
          <p className="whitespace-pre-wrap text-sm text-text-primary">{version.contextNotes}</p>
        ) : (
          <EmptyFieldPrompt
            hint="Org-level context — re-org, growth stage, recent exits, anything situational."
            onEdit={onEdit}
          />
        )}
      </div>
      <div>
        <span className="block text-xs text-text-faint">Success outcomes</span>
        {hasOutcomes ? (
          <ul className="mt-1 space-y-1">
            {version.successOutcomes.map((outcome) => (
              <li key={outcome} className="flex items-start gap-2 text-sm text-text-primary">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
                {outcome}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyFieldPrompt
            hint="Concrete deliverables in the first 12-18 months. Feed the assessment matrix."
            onEdit={onEdit}
          />
        )}
      </div>
      <div>
        <span className="block text-xs text-text-faint">Cultural fit</span>
        {version.culturalFitNotes ? (
          <p className="whitespace-pre-wrap text-sm text-text-primary">
            {version.culturalFitNotes}
          </p>
        ) : (
          <EmptyFieldPrompt
            hint="Values and working style. Feeds persona generation and the assessment guide."
            onEdit={onEdit}
          />
        )}
      </div>
      <div>
        <span className="block text-xs text-text-faint">Motivation fit</span>
        {version.motivationFitNotes ? (
          <p className="whitespace-pre-wrap text-sm text-text-primary">
            {version.motivationFitNotes}
          </p>
        ) : (
          <EmptyFieldPrompt
            hint="What makes this role attractive — used to brief candidates and shape outreach copy."
            onEdit={onEdit}
          />
        )}
      </div>
    </div>
  );
}

function SalarySummary({ version, onEdit }: SummaryProps) {
  const hasAnySalary =
    version.salaryBase != null ||
    version.salaryBonus ||
    version.salaryEquity ||
    version.salaryBenefits ||
    version.targetStartDate;

  const formatSalary = (amount: number, currency: string | null) => {
    try {
      return new Intl.NumberFormat("en", {
        style: "currency",
        currency: currency || "GBP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currency || "GBP"} ${amount.toLocaleString()}`;
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center gap-1 text-xs font-medium text-text-secondary">
        <Banknote size={14} />
        Compensation &amp; Start Date
      </div>
      {hasAnySalary ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
          {version.salaryBase != null && (
            <div>
              <span className="text-text-faint">Base: </span>
              <span className="text-text-primary">
                {formatSalary(version.salaryBase, version.salaryCurrency)}
              </span>
            </div>
          )}
          {version.salaryBonus && (
            <div>
              <span className="text-text-faint">Bonus: </span>
              <span className="text-text-primary">{version.salaryBonus}</span>
            </div>
          )}
          {version.salaryEquity && (
            <div>
              <span className="text-text-faint">Equity: </span>
              <span className="text-text-primary">{version.salaryEquity}</span>
            </div>
          )}
          {version.salaryBenefits && (
            <div>
              <span className="text-text-faint">Benefits: </span>
              <span className="text-text-primary">{version.salaryBenefits}</span>
            </div>
          )}
          {version.targetStartDate && (
            <div>
              <span className="text-text-faint">Target start: </span>
              <span className="text-text-primary">
                {new Date(`${version.targetStartDate}T00:00:00`).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      ) : (
        <EmptyFieldPrompt
          hint="Anchors candidate fit and informs outreach decline-handling."
          onEdit={onEdit}
        />
      )}
    </div>
  );
}

function ClientProcessSummary({ version, onEdit }: SummaryProps) {
  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center gap-1 text-xs font-medium text-text-secondary">
        <ClipboardCheck size={14} />
        Client Assessment &amp; Background Checks
      </div>
      <div className="space-y-2">
        <div>
          <span className="block text-xs text-text-faint">Assessment Process</span>
          {version.assessmentProcess ? (
            <p className="whitespace-pre-wrap text-sm text-text-primary">
              {version.assessmentProcess}
            </p>
          ) : (
            <EmptyFieldPrompt
              hint="Interview stages, panels, and decision-making process. Sets candidate expectations and feeds the selection-process editor."
              onEdit={onEdit}
            />
          )}
        </div>
        <div>
          <span className="block text-xs text-text-faint">Background Checks</span>
          {version.backgroundCheckRequirements ? (
            <p className="whitespace-pre-wrap text-sm text-text-primary">
              {version.backgroundCheckRequirements}
            </p>
          ) : (
            <EmptyFieldPrompt
              hint="DBS checks, referencing, right-to-work verification. Flagged to candidates ahead of offer."
              onEdit={onEdit}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AdditionalNotesSection({ version, onEdit }: SummaryProps) {
  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 text-xs font-medium text-text-secondary">Additional notes</div>
      {version.additionalNotes ? (
        <BriefRenderer content={version.additionalNotes} />
      ) : (
        <EmptyFieldPrompt
          hint="Additional notes that do not fit into the above categories."
          onEdit={onEdit}
        />
      )}
    </div>
  );
}

function IntakeNotesDisclosure({ version }: { version: Version }) {
  const [open, setOpen] = useState(false);
  if (!version.rawBriefNotes) return null;
  return (
    <div className="mt-3 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-text-faint hover:text-text-secondary"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Original intake notes
      </button>
      {open && (
        <div className="mt-2">
          <p className="text-xs italic text-text-faint">
            The raw notes this brief was structured from — kept for reference.
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-text-muted">
            {version.rawBriefNotes}
          </p>
        </div>
      )}
    </div>
  );
}
